import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import React from "react";

const RequiredChecksStep = ({ onValidChange }: { onValidChange: (isValid: boolean, agreementData?: any) => void }) => {
  const [isPolicyOpen, setIsPolicyOpen] = useState(false);
  const [animalTermsAgreed, setAnimalTermsAgreed] = useState(false);
  const [lmoTermsAgreed, setLmoTermsAgreed] = useState(false);
  const [orderCautionAgreed, setOrderCautionAgreed] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);

  const allAgreed = animalTermsAgreed && lmoTermsAgreed && orderCautionAgreed && privacyAgreed;

  // Notify parent component when validation state changes
  React.useEffect(() => {
    const agreementData = {
      animalTermsAgreed,
      lmoTermsAgreed,
      orderCautionAgreed,
      privacyAgreed,
    };
    onValidChange(allAgreed, agreementData);
  }, [allAgreed, animalTermsAgreed, lmoTermsAgreed, orderCautionAgreed, privacyAgreed, onValidChange]);
  return (
    <div className="px-2 md:px-0">
      <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">Step 3. 필수 약관 확인</h2>
      
      <div className="space-y-4 md:space-y-6">
        {/* Detailed Requirements */}
        <div className="space-y-4 md:space-y-6">
          <div>
            <h3 className="font-semibold mb-3 text-base md:text-lg">1. 동물 사용 약관 확인</h3>
            <ul className="text-sm space-y-2 list-disc list-inside mb-4">
              <li><span className="font-bold">일반 약관</span>: <a 
                href="https://www.jax.org/about-us/legal-information/terms-and-conditions-of-product-use" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-primary hover:underline break-all"
              >
                https://www.jax.org/about-us/legal-information/terms-and-conditions-of-product-use
              </a>에서 확인 가능합니다. (<a 
                href="https://centraltg.co.kr/notice/0ac9d230-539f-47be-80c4-9aaf84943969" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-primary hover:underline font-medium"
              >일반 약관 요약본 확인하기</a>)</li>
              <li><span className="font-bold">계통별 약관</span>: <span className="text-primary font-medium">[계통 상세페이지 → Legal → Terms of use]</span> 에서 확인 가능합니다.</li>
              <li className="text-destructive font-semibold">사용약관에는 동물 사용 제한 등 중요한 사항이 포함되어 있으니 반드시 확인해야 합니다. 미확인으로 인한 불이익은 연구자 본인 책임입니다.</li>
            </ul>
            <div className="flex items-start space-x-3 md:space-x-4 bg-muted/30 p-3 md:p-4 rounded-lg border">
              <Checkbox 
                id="animal-terms" 
                checked={animalTermsAgreed}
                onCheckedChange={(checked) => setAnimalTermsAgreed(checked as boolean)}
                className="h-5 w-5 border-2 mt-0.5 flex-shrink-0"
              />
              <Label htmlFor="animal-terms" className="text-sm md:text-base font-semibold leading-tight">
                JAX® 의 동물 사용 약관 내용을 확인하였으며 이에 동의합니다.
              </Label>
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-3 text-base md:text-lg">2. LMO(유전자변형생물체) 신고 확인</h3>
            <ul className="text-sm space-y-2 list-disc list-inside mb-4">
              <li>2022년 4월 1일부터 LMO 신고는 연구자 직접 접수만 가능하며, 신고대행은 불가합니다.</li>
              <li>본 동물이 LMO 대상일 경우, 신고 완료 없이는 수입이 불가능합니다.</li>
              <li><a 
                href="/notice/85fdce16-a21a-49af-8f7b-f84e0fd30936" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-primary hover:underline"
              >
                LMO 신고방법 확인하기
              </a></li>
            </ul>
            <div className="flex items-start space-x-3 md:space-x-4 bg-muted/30 p-3 md:p-4 rounded-lg border">
              <Checkbox 
                id="lmo-terms" 
                checked={lmoTermsAgreed}
                onCheckedChange={(checked) => setLmoTermsAgreed(checked as boolean)}
                className="h-5 w-5 border-2 mt-0.5 flex-shrink-0"
              />
              <Label htmlFor="lmo-terms" className="text-sm md:text-base font-semibold leading-tight">
                본 주문이 LMO 동물일 경우 관련 법규에 따른 신고 절차를 진행해야 하며, LMO 안내를 확인했습니다.
              </Label>
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-3 text-base md:text-lg">3. 동물 발주 시 주의사항</h3>
            <ul className="text-sm space-y-2 list-disc list-inside mb-4">
              <li>Phenotype 및 체중은 보장되지 않습니다.</li>
              <li>운송 사고 또는 발주 취소 시 환불·재공급은 불가하며, 폐사 수량만큼 공제 또는 동물대금과 동일한 취소 수수료가 부과됩니다.</li>
              <li>항공 스케줄, 천재지변, 현지 생산 상황 등에 따라 배송이 지연·변경될 수 있습니다.</li>
            </ul>
            <div className="flex items-start space-x-3 md:space-x-4 bg-muted/30 p-3 md:p-4 rounded-lg border">
              <Checkbox 
                id="order-caution" 
                checked={orderCautionAgreed}
                onCheckedChange={(checked) => setOrderCautionAgreed(checked as boolean)}
                className="h-5 w-5 border-2 mt-0.5 flex-shrink-0"
              />
              <Label htmlFor="order-caution" className="text-sm md:text-base font-semibold leading-tight">
                동물발주 시 주의사항을 확인하였으며 이에 동의합니다.
              </Label>
            </div>
          </div>
        </div>

        {/* Personal Information Agreement - Moved to bottom */}
        <div className="space-y-4">
          <h3 className="font-semibold mb-3 text-base md:text-lg">개인정보 수집 및 이용 동의</h3>
          <Collapsible open={isPolicyOpen} onOpenChange={setIsPolicyOpen}>
            <CollapsibleTrigger asChild>
              <Button 
                type="button" 
                variant="outline" 
                className="w-full justify-between text-sm md:text-base"
              >
                개인정보 수집 및 이용 동의 / 처리방침
                {isPolicyOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4">
              <div className="max-h-64 overflow-y-auto p-3 md:p-4 border rounded-md bg-muted/50 text-xs md:text-sm">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2">[개인정보 수집 및 이용 동의]</h4>
                    <p><strong>Ⅰ. 수집 목적:</strong> 재화 또는 서비스 제공 물품배송, 서비스 제공, 계약서・청구서 발송, 광고 콘텐츠 제공, 맞춤서비스 제공, 요금결제・정산</p>
                    <p><strong>Ⅱ. 수집 항목:</strong> 성명, 소속, 전화번호, 이메일, 구매내역</p>
                    <p><strong>Ⅲ. 보유 및 이용기간:</strong> 수집일부터 5년까지</p>
                    <p><strong>Ⅳ.</strong> 귀하는 위와 같이 개인정보를 수집 및 이용하는데 동의를 거부할 권리가 있습니다. 단 미동의시 상담 및 배송 서비스관련 업무에 불이익을 받을 수 있습니다.</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-2">[센트럴티지㈜ 개인정보 처리방침]</h4>
                    <p className="mb-2">센트럴티지㈜ 은(는) 정보주체의 자유와 권리 보호를 위해 ｢개인정보 보호법｣ 및 관계 법령이 정한 바를 준수하여, 적법하게 개인정보를 처리하고 안전하게 관리하고 있습니다. 이에 ｢개인정보 보호법｣ 제30조에 따라 정보주체에게 개인정보의 처리와 보호에 관한 절차 및 기준을 안내하고, 이와 관련한 고충을 신속하고 원활하게 처리할 수 있도록 하기 위하여 다음과 같이 개인정보 처리방침을 수립·공개합니다.</p>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">□ 개인정보의 처리목적</h4>
                    <p className="mb-2">센트럴티지㈜ 은(는) 다음의 목적을 위하여 개인정보를 처리합니다. 처리하고 있는 개인정보는 다음의 목적 외의 용도로는 이용되지 않으며, 이용 목적이 변경되는 경우에는 ｢개인정보 보호법｣ 제18조에 따라 별도의 동의를 받는 등 필요한 조치를 이행할 예정입니다.</p>
                    <p>1. 재화 또는 서비스 제공: 물품배송, 서비스 제공, 계약서·청구서 발송, 콘텐츠 제공, 맞춤형 추천서비스 제공, 결제·정산의 목적으로 개인정보를 처리합니다.</p>
                    <p>2. 서비스 개선 및 분석: 서비스 이용에 대한 분석, 인구통계학적 분석 및 서비스 개선을 목적으로 개인정보를 처리합니다.</p>
                    <p>3. 서비스 개발: 기존 서비스와 별개의 신규 서비스 개발 목적으로 개인정보를 처리합니다.</p>
                  </div>

                  <div>
                    <p className="mb-2">센트럴티지㈜ 은(는) 다음과 같은 개인정보 법적 근거로 정보주체의 개인정보를 수집 및 이용합니다.</p>
                    <p className="mb-1"><strong>1. 정보주체의 동의를 받지 않고 처리하는 개인정보 항목</strong> 은(는) 다음의 개인정보 항목을 정보주체의 동의없이 처리하고 있습니다.</p>
                    <p className="ml-4">1. 판매 상품에 대한 A/S 상담</p>
                    <p className="ml-4">· 법적 근거: 개인정보 보호법 제15조 제1항 제4호('계약 이행')</p>
                    <p className="ml-4">· 수집・이용 항목: 성명, 소속, 전화번호, 이메일, 구매내역</p>
                    <p className="ml-4">2. 주문 및 결제 처리</p>
                    <p className="ml-4">· 법적 근거: 개인정보 보호법 제15조 제1항 제4호('계약 이행')</p>
                    <p className="ml-4">· 수집・이용 항목: 성명, 소속, 전화번호, 이메일, 구매내역</p>
                    <p className="mb-1 mt-2"><strong>2. 정보주체의 동의를 받아 처리하는 개인정보 항목</strong> 은(는) 다음의 개인정보 항목을 「개인정보 보호법」 제15조 제1항 제1호 및 제22조 제1항 제7호에 따라 정보주체의 동의를 받아 처리하고 있습니다.</p>
                    <p className="ml-4">1. 이벤트 참여 및 경품 응모</p>
                    <p className="ml-4">· 수집・이용 항목: 성명, 소속, 전화번호, 이메일, 구매내역</p>
                    <p className="ml-4">2. 서비스 홍보 및 판매 권유를 위한 개인정보 처리</p>
                    <p className="ml-4">· 수집・이용 항목: 성명, 소속, 전화번호, 이메일, 구매내역</p>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">□ 개인정보의 처리 및 보유 기간</h4>
                    <p>① 센트럴티지㈜ 은(는) 법령에 따른 개인정보 보유·이용 기간 또는 정보주체로부터 개인정보를 수집 시에 동의받은 개인정보 보유·이용 기간 내에서 개인정보를 처리·보유합니다.</p>
                    <p>② 각각의 개인정보 처리 및 보유 기간은 다음과 같습니다.</p>
                    <p className="ml-4">1. 재화 또는 서비스 제공: 재화·서비스 공급완료 후 5년</p>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">□ 개인정보의 파기 절차 및 방법</h4>
                    <p>① 센트럴티지㈜ 은(는) 개인정보 보유기간의 경과, 처리목적 달성 등 개인정보가 불필요하게 되었을 때에는 지체없이 해당 개인정보를 파기합니다.</p>
                    <p>② 정보주체로부터 동의받은 개인정보 보유기간이 경과하거나 처리목적이 달성되었음에도 불구하고 다른 법령에 따라 개인정보를 계속 보존하여야 하는 경우에는, 해당 개인정보를 별도의 데이터베이스(DB)로 옮기거나 보관장소를 달리하여 보존합니다.</p>
                    <p className="ml-4">※ 다른 법령에 따라 보존하는 개인정보의 항목과 보존 근거는 '5. 개인정보의 처리 및 보유 기간' 항목에서 확인 가능</p>
                    <p>③ 개인정보 파기의 절차 및 방법은 다음과 같습니다.</p>
                    <p className="ml-4">1. 파기절차</p>
                    <p className="ml-4">센트럴티지㈜ 은(는) 파기 사유가 발생한 개인정보를 선정하고, 센트럴티지㈜ 의 개인정보 보호책임자의 승인을 받아 개인정보를 파기합니다.</p>
                    <p className="ml-4">2. 파기방법</p>
                    <p className="ml-4">센트럴티지㈜ 은(는) 전자적 파일 형태로 기록·저장된 개인정보는 기록을 재생할 수 없도록 파기하며, 종이 문서에 기록·저장된 개인정보는 분쇄기로 분쇄하거나 소각하여 파기합니다.</p>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">□ 개인정보의 안전성 확보조치</h4>
                    <p>센트럴티지㈜ 은(는) 개인정보의 안전성 확보를 위해 다음과 같은 조치를 취하고 있습니다.</p>
                    <p className="ml-4">1. 관리적 조치: 내부 관리계획 수립·시행</p>
                    <p className="ml-4">2. 기술적 조치: 개인정보처리시스템에 대한 등의 접근 권한의 관리, 접속기록의 보관 및 점검, 보안프로그램 설치 및 갱신, 개인정보처리시스템의 취약점 점검 및 보완</p>
                    <p className="ml-4">3. 물리적 조치: 전산실, 자료보관실 등의 접근통제, 서류·보조저장매체 등을 잠금장치가 있는 안전한 장소에 보관</p>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">□ 정보주체와 법정대리인의 권리·의무 및 행사방법</h4>
                    <p>① 정보주체는 센트럴티지㈜ 에 대해 언제든지 개인정보 열람·전송·정정·삭제·처리정지 및 동의 철회 등 을 요구(이하 "권리 행사"라 함)할 수 있습니다.</p>
                    <p className="ml-4">※ 14세 미만 아동의 권리 행사는 법정대리인이 직접 해야 하며, 14세 이상의 미성년자인 정보주체는 정보주체의 개인정보 에 관하여 미성년자 본인이 권리를 행사하거나 법정대리인을 통하여 권리를 행사할 수 있습니다.</p>
                    <p>② 권리 행사는 센트럴티지㈜ 에 대해 「개인정보 보호법 시행령」 제41조제1항에 따라 서면, 전화, 전자우편, 팩스(FAX), 인터넷 등을 통하여 하실 수 있으며, &lt;개인정보처리자명&gt;은(는) 이에 대해 지체없이 조치하겠습니다.</p>
                    <p className="ml-4">- 정보주체는 언제든지 메일 문의를 통해 열람을 요청할 수 있습니다.</p>
                    <p>③ 권리 행사는 정보주체의 법정대리인이나 위임을 받은 자 등 대리인을 통하여 하실 수도 있습니다. 이 경우 "개인정보 처리 방법에 관한 고시" [별지 11] 서식에 따른 위임장을 제출하셔야 합니다.</p>
                    <p>④ 정보주체가 개인정보 열람 및 처리 정지를 요구할 권리는 「개인정보 보호법」 제35조제4항 및 제37조제2항에 의하여 제한될 수 있습니다.</p>
                    <p>⑤ 다른 법령에서 그 개인정보가 수집 대상으로 명시되어 있는 경우에는 해당 개인정보의 삭제를 요구할 수 없습니다.</p>
                    <p>⑥ 센트럴티지㈜ 은(는) 권리 행사를 한 자가 본인이거나 정당한 대리인인지를 확인합니다.</p>
                    <p>⑦ 정보주체는 권리 행사를 아래의 부서에 할 수 있습니다. 센트럴티지㈜ 은(는) 정보주체로부터 권리 행사를 청구받은 날로부터 10일(전송요구의 경우 지체 없이) 이내 회신하겠습니다.</p>
                    <p className="mt-2"><strong>▶ 개인정보 권리 행사 청구 접수·처리 담당자</strong></p>
                    <p className="ml-4">담당자: 이수영</p>
                    <p className="ml-4">주소: 서울특별시 서초구 서초대로 396, 강남빌딩 2층 222호</p>
                    <p className="ml-4">연락처: +(82) 2-577-1684, 이메일: info@centralTG.co.kr, 팩스: +(82) 2-577-1865</p>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">□ 개인정보 보호책임자</h4>
                    <p>① 센트럴티지㈜ 은(는) 개인정보 처리에 관한 업무를 총괄해서 책임지고, 개인정보 처리와 관련한 정보주체의 불만처리 및 피해구제 등을 위하여 아래와 같이 개인정보 보호책임자를 지정하고 있습니다.</p>
                    <p className="mt-2"><strong>▶ 개인정보 보호책임자</strong></p>
                    <p className="ml-4">담당자: 이수영</p>
                    <p className="ml-4">주소: 서울특별시 서초구 서초대로 396, 강남빌딩 2층 222호</p>
                    <p className="ml-4">연락처: +(82) 2-577-1684, 이메일: info@centralTG.co.kr, 팩스: +(82) 2-577-1865</p>
                    <p className="mt-2"><strong>▶ 개인정보보호 담당자</strong></p>
                    <p className="ml-4">담당자: 이수영</p>
                    <p className="ml-4">주소: 서울특별시 서초구 서초대로 396, 강남빌딩 2층 222호</p>
                    <p className="ml-4">연락처: +(82) 2-577-1684, 이메일: info@centralTG.co.kr, 팩스: +(82) 2-577-1865</p>
                    <p className="mt-2">② 정보주체는 센트럴티지㈜ 의 서비스(또는 사업)를 이용하시면서 발생한 모든 개인정보보호 관련 문의, 불만처리, 피해구제 등에 관한 사항을 개인정보 보호책임자 및 개인정보보호 담당부서로 문의할 수 있습니다.</p>
                    <p>센트럴티지㈜ 은(는) 정보주체의 문의에 대해 지체없이 답변 및 처리해 드릴 것입니다.</p>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">□ 개인정보 처리방침의 변경</h4>
                    <p>① 이 개인정보 처리방침은 2025.04.21 부터 적용됩니다.</p>
                    <p>② 이전의 개인정보 처리방침은 아래에서 확인하실 수 있습니다.</p>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">[센트럴티지㈜ 개인정보 처리방침]</h4>
                    <p className="mb-2">센트럴티지㈜ 은(는) 정보주체의 자유와 권리 보호를 위해 「개인정보 보호법」 및 관계 법령이 정한 바를 준수하여, 적법하게 개인정보를 처리하고 안전하게 관리하고 있습니다. 이에 「개인정보 보호법」 제30조에 따라 정보주체에게 개인정보의 처리와 보호에 관한 절차 및 기준을 안내하고, 이와 관련한 고충을 신속하고 원활하게 처리할 수 있도록 하기 위하여 다음과 같이 개인정보 처리방침을 수립・공개합니다.</p>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">Ⅰ. 개인정보의 처리목적</h4>
                    <p className="mb-2">센트럴티지㈜ 은(는) 다음의 목적을 위하여 개인정보를 처리합니다. 처리하고 있는 개인정보는 다음의 목적 이외의 용도로는 이용되지 않으며, 이용 목적이 변경되는 경우에는 「개인정보 보호법」 제18조에 따라 별도의 동의를 받는 등 필요한 조치를 이행할 예정입니다.</p>
                    <p>1. 재화 또는 서비스 제공 물품배송, 서비스 제공, 계약서・청구서 발송, 광고 콘텐츠 제공, 맞춤서비스 제공, 요금결제・정산의 목적으로 개인정보를 처리합니다.</p>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">Ⅱ. 처리하는 개인정보 항목</h4>
                    <p className="mb-2">센트럴티지㈜ 은(는) 서비스 제공을 위해 필요 최소한의 범위에서 개인정보를 수집・이용합니다.</p>
                    <p className="mb-1"><strong>1. 정보주체의 동의를 받지 않고 처리하는 개인정보 항목</strong> 은(는) 다음의 개인정보 항목을 정보주체의 동의없이 처리하고 있습니다.</p>
                    <p className="ml-4">1. 판매 상품에 대한 A/S 상담</p>
                    <p className="ml-4">· 법적 근거: 개인정보 보호법 제15조 제1항 제4호('계약 이행')</p>
                    <p className="ml-4">· 수집・이용 항목: 성명, 소속, 전화번호, 이메일, 구매내역</p>
                    <p className="ml-4">2. 주문 및 결제 처리</p>
                    <p className="ml-4">· 법적 근거: 개인정보 보호법 제15조 제1항 제4호('계약 이행')</p>
                    <p className="ml-4">· 수집・이용 항목: 성명, 소속, 전화번호, 이메일, 구매내역</p>
                    <p className="mb-1 mt-2"><strong>2. 정보주체의 동의를 받아 처리하는 개인정보 항목</strong> 은(는) 다음의 개인정보 항목을 「개인정보 보호법」 제15조 제1항 제1호 및 제22조 제1항 제7호에 따라 정보주체의 동의를 받아 처리하고 있습니다.</p>
                    <p className="ml-4">1. 이벤트 참여 및 경품 응모</p>
                    <p className="ml-4">· 수집・이용 항목: 성명, 소속, 전화번호, 이메일, 구매내역</p>
                    <p className="ml-4">2. 서비스 홍보 및 판매 권유를 위한 개인정보 처리</p>
                    <p className="ml-4">· 수집・이용 항목: 성명, 소속, 전화번호, 이메일, 구매내역</p>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">Ⅲ. 개인정보의 처리 및 보유기간</h4>
                    <p>① 센트럴티지㈜ 은(는) 법령에 따른 개인정보 보유・이용기간 또는 정보주체로부터 개인정보를 수집 시에 동의 받은 개인정보 보유・이용기간 내에서 개인정보를 처리・보유합니다.</p>
                    <p>② 각각의 개인정보 처리 및 보유 기간은 다음과 같습니다.</p>
                    <p className="ml-4">1. 재화 또는 서비스 제공: 재화·서비스 공급완료 후 5년</p>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">Ⅳ. 개인정보의 파기 절차 및 방법</h4>
                    <p>① 센트럴티지㈜ 은(는) 개인정보 보유기간의 경과, 처리목적 달성 등 개인정보가 불필요하게 되었을 때에는 지체없이 해당 개인정보를 파기합니다.</p>
                    <p>② 정보주체로부터 동의받은 개인정보 보유기간이 경과하거나 처리목적이 달성되었음에도 불구하고 다른 법령에 따라 개인정보를 계속 보존하여야 하는 경우에는, 해당 개인정보를 별도의 데이터베이스(DB)로 옮기거나 보관장소를 달리하여 보존합니다.</p>
                    <p className="ml-4">※ 다른 법령에 따라 보존하는 개인정보의 항목과 보존 근거는 "5. 개인정보의 처리 및 보유기간' 항목에서 확인 가능</p>
                    <p>③ 개인정보 파기의 절차 및 방법은 다음과 같습니다.</p>
                    <p className="ml-4">1. 파기절차</p>
                    <p className="ml-4">센트럴티지㈜ 은(는) 파기 사유가 발생한 개인정보를 선정하고, 센트럴티지㈜ 개인정보 보호책임자의 승인을 받아 개인정보를 파기합니다.</p>
                    <p className="ml-4">2. 파기방법</p>
                    <p className="ml-4">센트럴티지㈜ 은(는) 전자적 파일 형태로 기록・저장된 개인정보는 기록을 재생할 수 없도록 파기하며, 종이 문서에 기록・저장된 개인정보는 분쇄기로 분쇄하거나 소각하여 파기합니다.</p>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">Ⅴ. 개인정보의 안전성 확보조치</h4>
                    <p>① 센트럴티지㈜ 은(는) 개인정보의 안전성 확보를 위해 다음과 같은 조치를 취하고 있습니다.</p>
                    <p className="ml-4">1. 관리적 조치: 내부관리계획 수립・시행, 전담조직 운영, 정기적 직원 교육</p>
                    <p className="ml-4">2. 기술적 조치: 개인정보처리시스템 등의 접근권한 관리, 개인정보의 암호화</p>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">Ⅵ. 정보주체와 법정대리인의 권리・의무 및 행사방법</h4>
                    <p>① 정보주체는 센트럴티지㈜ 에 대해 언제든지 개인정보 열람・정정・ 삭제・처리정지 및 철회 요구, 자동화된 결정에 대한 거부 또는 설명 요구 등의 권리를 행사(이하 "권리 행사"라 함)할 수 있습니다.</p>
                    <p className="ml-4">※ 14세 미만 아동에 관한 개인정보의 열람등 요구는 법정대리인이 직접 해야 하며, 14세 이상의 미성년자인 정보주체는 정보주체의 개인정보에 관하여 미성년자 본인이 권리를 행사하거나 법정대리인을 통하여 권리를 행사할 수도 있습니다.</p>
                    <p>② 권리 행사는 센트럴티지㈜ 에 대해 「개인정보 보호법」 시행령 제41조 제1항에 따라 서면, 전자우편, 모사전송(FAX) 등을 통하여 하실 수 있으며, 센트럴티지㈜ 은(는) 이에 대해 지체없이 조치하겠습니다.</p>
                    <p>③ 권리 행사는 정보주체의 법정대리인이나 위임을 받은 자 등 대리인을 통하여 하실 수도 있습니다. 이 경우 "개인정보 처리 방법에 관한 고시" 별지 제11호 서식에 따른 위임장을 제출하셔야 합니다.</p>
                    <p>④ 정보주체가 개인정보 열람 및 처리 정지를 요구할 권리는 「개인정보 보호법」 제35조 제4항 및 제37조 제2항에 의하여 제한될 수 있습니다.</p>
                    <p>⑤ 다른 법령에서 그 개인정보가 수집 대상으로 명시되어 있는 경우에는 해당 개인정보의 삭제를 요구할 수 없습니다.</p>
                    <p>⑥ 자동화된 결정이 이루어진다는 사실에 대해 정보주체의 동의를 받았거나, 계약 등을 통해 미리 알린 경우, 법률에 명확히 규정이 있는 경우에는 자동화된 결정에 대한 거부는 인정되지 않으며 설명 및 검토 요구만 가능합니다.</p>
                    <p className="ml-4">- 또한 자동화된 결정에 대한 거부・설명 요구는 다른 사람의 생명・신체・재산과 그 밖의 이익을 부당하게 침해할 우려가 있는 등 정당한 사유가 있는 경우에는 그 요구가 거절될 수 있습니다.</p>
                    <p>⑦ 센트럴티지㈜ 은(는) 권리 행사를 한 자가 본인이거나 정당한 대리인인지를 확인합니다.</p>
                    <p>⑧ 센트럴티지㈜ 은(는) 권리 행사를 아래의 부서에 할 수 있습니다. 센트럴티지㈜ 은(는) 정보주체의 권리 행사가 신속하게 처리되도록 노력하겠습니다.</p>
                    <p className="mt-2"><strong>▶ 개인정보 열람 등 청구 접수・처리 부서</strong></p>
                    <p className="ml-4">부서명: 전략기획실</p>
                    <p className="ml-4">주소: 서울특별시 서초구 서초대로 396, 강남빌딩 2층 222호</p>
                    <p className="ml-4">연락처: +(82) 2-577-1684, 이메일: info@centralTG.co.kr, 팩스: +(82) 2-577-1865</p>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">Ⅶ. 개인정보 보호책임자</h4>
                    <p>① 센트럴티지㈜ 은(는) 개인정보 처리에 관한 업무를 총괄해서 책임지고, 개인정보 처리와 관련한 정보주체의 불만처리 및 피해구제 등을 위하여 아래와 같이 개인정보 보호책임자를 지정하고 있습니다.</p>
                    <p className="mt-2"><strong>▶ 개인정보 보호책임자</strong></p>
                    <p className="ml-4">성명: 이수영</p>
                    <p className="ml-4">연락처: +(82) 2-577-1684, 이메일: info@centralTG.co.kr, 팩스: +(82) 2-577-1865</p>
                    <p className="mt-2">② 정보주체는 센트럴티지㈜ 의 서비스(또는 사업)을 이용하시면서 발생한 모든 개인정보보호 관련 문의, 불만처리, 피해구제 등에 관한 사항을 개인정보 보호책임자 및 담당부서로 문의할 수 있습니다. 센트럴티지㈜ 은(는) 정보주체의 문의에 대해 지체없이 답변 및 처리해드릴 것입니다.</p>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">Ⅷ. 개인정보 처리방침의 변경</h4>
                    <p>① 이 개인정보 처리방침은 2024.09.05 부터 적용됩니다.</p>
                    <p>② 이후 개인정보 처리방침은 변경 시, 아래 추가 기재하도록 하겠습니다.</p>
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
          
          <div className="flex items-start space-x-2 md:space-x-3 p-3 md:p-4 rounded-lg">
            <Checkbox 
              id="privacy-agreement"
              checked={privacyAgreed}
              onCheckedChange={(checked) => setPrivacyAgreed(checked as boolean)}
              className="h-5 w-5 border-2 flex-shrink-0 mt-0.5"
            />
            <Label 
              htmlFor="privacy-agreement" 
              className="text-sm md:text-base font-semibold leading-tight peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              개인정보 수집 및 이용에 동의합니다.
            </Label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequiredChecksStep;